'use client';

import { motion } from 'framer-motion';
import {
	ChartBar as BarChart3,
	Building2,
	CircleCheck as CheckCircle2,
	Clock,
	type LucideIcon,
	Package,
	Settings,
	ShieldCheck,
	ShoppingCart,
	Tags,
	Truck,
	Wrench,
} from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import ContactNew from '@/components/ContactNew';
import FrequentlyAskedQuestions from '@/components/FrequentlyAskedQuestions';
import { TextImageSection } from '@/components/TextImageSection';
import { fadeIn } from '@/lib/animations';

export interface Service {
	title: string;
	description: string;
	icon: string;
	color: string;
	iconColor: string;
	highlight?: string;
}

export interface FAQ {
	question: string;
	answer: string;
}

export interface WORCSFeature {
	icon: string;
	title: string;
	description: string;
}

export interface ContactFeature {
	title: string;
	description: string;
}

export interface WarehousingValueAddedServicesProps {
	subtitle: string;
	title: string;
	description: string;
	services: Service[];
	worcsSubtitle: string;
	worcsTitle: string;
	worcsDescription: string;
	worcsImageUrl: string;
	worcsImageAlt: string;
	worcsFeatures: WORCSFeature[];
	faqTitle: string;
	faqDescription: string;
	faqs: FAQ[];
	contactTitle: string;
	contactDescription: string;
	contactFeatures: ContactFeature[];
}

const iconMap: Record<string, LucideIcon> = {
	ShieldCheck,
	Truck,
	ShoppingCart,
	Building2,
	Package,
	BarChart3,
	Tags,
	Clock,
	Settings,
	Wrench,
	CheckCircle2,
};

export function WarehousingValueAddedServices({
	subtitle,
	title,
	description,
	services,
	worcsSubtitle,
	worcsTitle,
	worcsDescription,
	worcsImageUrl,
	worcsImageAlt,
	worcsFeatures,
	faqTitle,
	faqDescription,
	faqs,
	contactTitle,
	contactDescription,
	contactFeatures,
}: WarehousingValueAddedServicesProps) {
	const [ref] = useInView({
		triggerOnce: true,
		threshold: 0.1,
		rootMargin: '100px 0px',
	});

	return (
		<>
			<section className="py-24 bg-white">
				<div className="container mx-auto px-4 md:px-6">
					<motion.div
						ref={ref}
						initial="hidden"
						whileInView="show"
						viewport={{ once: true, amount: 0.3 }}
						variants={fadeIn('up')}
						className="text-center mb-16"
					>
						<h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-2">
							{subtitle}
						</h2>
						<h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
							{title}
						</h3>
						<p className="max-w-3xl mx-auto text-gray-700 text-lg leading-relaxed">
							{description}
						</p>
					</motion.div>

					{/* Services Grid */}
					<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
						{services.map((service, index) => {
							const ServiceIcon = iconMap[service.icon] || Package;
							return (
								<motion.div
									key={index}
									initial="hidden"
									whileInView="show"
									viewport={{ once: true, amount: 0.3 }}
									variants={fadeIn('up', 0.1 * index)}
									className={`${service.color} border-2 rounded-xl p-8 hover:shadow-lg transition-all duration-300 group`}
								>
									<div
										className={`w-16 h-16 ${service.color.replace('50', '100')} rounded-xl flex items-center justify-center ${service.iconColor} mb-6 group-hover:scale-110 transition-transform`}
									>
										<ServiceIcon size={32} />
									</div>

									<h4 className="text-xl font-bold text-gray-900 mb-4">
										{service.title}
									</h4>

									<p className="text-gray-700 mb-4 leading-relaxed">
										{service.description}
									</p>

									{service.highlight && (
										<div className="bg-white/80 rounded-lg p-3 border border-gray-200">
											<p className="text-sm font-semibold text-gray-800">
												{service.highlight}
											</p>
										</div>
									)}
								</motion.div>
							);
						})}
					</div>

					{/* WORCS System Highlight */}
					<TextImageSection
						subtitle={worcsSubtitle}
						title={worcsTitle}
						content={
							<>
								<p className="mb-8">{worcsDescription}</p>
								<div className="grid gap-6">
									{worcsFeatures.map((feature, index) => {
										const FeatureIcon = iconMap[feature.icon] || Package;
										return (
											<div key={index} className="flex items-start gap-4">
												<div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center text-brand-600 flex-shrink-0">
													<FeatureIcon size={24} />
												</div>
												<div>
													<h4 className="text-xl font-semibold text-gray-900 mb-2">
														{feature.title}
													</h4>
													<p className="text-gray-700">
														{feature.description}
													</p>
												</div>
											</div>
										);
									})}
								</div>
							</>
						}
						imageUrl={worcsImageUrl}
						imageAlt={worcsImageAlt}
						buttonText="Contact Us Today"
						reverse={true}
						sectionClassName="!py-0"
					/>
				</div>
			</section>

			{/* FAQ Section */}
			<FrequentlyAskedQuestions
				subtitle="Frequently Asked Questions"
				title={faqTitle}
				description={faqDescription}
				faqs={faqs}
			/>

			{/* Contact Form Section */}
		</>
	);
}
